import type React from "react";
import { useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import type { AxiosError } from "axios";
import { isAxiosError } from "axios";
import { EmailAddress } from "@shared/schemas/email.schemas";
import { useAnonymousActions } from "@frontend/shared/features/actions/application/actions/use-anonymous-actions";
import { i18n } from "@frontend/i18n";

function getLoginErrorMessage(error: AxiosError): string {
  const status = error.response?.status;

  if (status === 400 || status === 404) {
    return i18n.t("login.error.invalidCredentials");
  }

  if (status !== undefined && status >= 500) {
    return i18n.t("login.error.serverError");
  }

  if (error.request !== undefined && error.response === undefined) {
    return i18n.t("login.error.networkError");
  }

  if (status !== undefined) {
    return i18n.t("login.error.failed", { code: String(status) });
  }

  return i18n.t("login.error.generic");
}

function getDefaultRedirect(): string {
  const pathname = window.location.pathname;

  if (pathname.startsWith("/admin")) {
    return "/admin/";
  }
  if (pathname.startsWith("/host")) {
    return "/host/";
  }

  return "/";
}

function deriveErrorMessage(error: Error): string {
  if (isAxiosError(error)) {
    return getLoginErrorMessage(error);
  }
  return i18n.t("login.error.generic");
}

interface LoginControllerReturn {
  readonly state: {
    readonly email: string;
    readonly password: string;
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly errorMessage: string | null;
    readonly ssoReturnUrl: string;
  };
  readonly actions: {
    readonly setEmail: (value: string) => void;
    readonly setPassword: (value: string) => void;
    readonly handleSubmit: (event: React.FormEvent) => void;
  };
}

export function useLoginController(): LoginControllerReturn {
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const returnUrl = searchParams.get("returnUrl");

  const loginMutation = useAnonymousActions().useLogin();

  const handleSubmit = useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();

      const defaultRedirect = getDefaultRedirect();
      const redirectUrl = returnUrl ?? defaultRedirect;

      loginMutation.mutate(
        {
          credentials: {
            email: EmailAddress.unsafeCreate(email),
            password,
          },
          redirectUrl,
        },
        {
          onSuccess: (result): void => {
            window.location.href = result.redirect;
          },
        },
      );
    },
    [email, password, returnUrl, loginMutation],
  );

  return {
    state: {
      email,
      password,
      isPending: loginMutation.isPending,
      isError: loginMutation.isError,
      errorMessage: loginMutation.isError
        ? deriveErrorMessage(loginMutation.error)
        : null,
      ssoReturnUrl: returnUrl ?? getDefaultRedirect(),
    },
    actions: {
      setEmail,
      setPassword,
      handleSubmit,
    },
  };
}
